/**
 * weights.ts — adjustable weight tables for the Roamer Intelligence Engine™.
 *
 * Every score in the engine is CALCULATED from these tables — nothing is
 * hardcoded at call sites. Tune here (or pass overrides) to rebalance the
 * whole system without touching scoring logic.
 */

import type { RoamerScoreCategory } from '../types';
import type { RoamerProfile } from '../RoamerProfile';

// ─── Roamer Score (city-level) base weights ───────────────────────────────────
// Relative importance before personalisation. Normalised at scoring time, so
// only ratios matter.

export const DEFAULT_ROAMER_SCORE_WEIGHTS: Record<RoamerScoreCategory, number> = {
  internet: 1.3,
  housing: 1.2,
  affordability: 1.2,
  safety: 1.2,
  healthcare: 0.9,
  transportation: 0.9,
  weather: 0.9,
  community: 1.0,
  remoteWork: 1.1,
  food: 0.9,
  lifestyle: 0.8,
  entertainment: 0.7,
  cost: 1.1,
};

/**
 * Personalise the base weights with the user's profile. Purely multiplicative
 * and clamped so no single preference can drown out the rest.
 */
export function personalizeRoamerScoreWeights(
  base: Record<RoamerScoreCategory, number>,
  profile: RoamerProfile,
): Record<RoamerScoreCategory, number> {
  const boost = (importance: number) => 0.7 + (importance / 100) * 0.9; // 0.7–1.6
  const w: Record<RoamerScoreCategory, number> = { ...base };

  if (profile.remoteWorker || profile.digitalNomad) {
    w.internet *= 1.3;
    w.remoteWork *= 1.3;
  }
  if (profile.preferredInternetMbps != null) w.internet *= 1.15;
  if (profile.needsCoworking) w.remoteWork *= 1.2;

  if (profile.monthlyBudgetUsd != null || profile.housingBudgetUsd != null) {
    w.affordability *= 1.2;
    w.cost *= 1.2;
    w.housing *= 1.1;
  }

  w.safety *= boost(profile.safetyPriority);
  w.transportation *= boost(profile.publicTransit);
  w.entertainment *= boost(profile.nightlife);
  w.lifestyle *= boost(Math.max(profile.nature, profile.beach));

  if (profile.travelStyle === 'family') {
    w.safety *= 1.2;
    w.healthcare *= 1.25;
  }

  // Clamp each weight to a sane band so personalisation stays a nudge.
  for (const k of Object.keys(w) as RoamerScoreCategory[]) {
    w[k] = Math.min(3, Math.max(0.2, w[k]));
  }
  return w;
}

// ─── Match Score thresholds ───────────────────────────────────────────────────

/** Criterion satisfaction ≥ this → surfaced as a positive "Why?" reason. */
export const REASON_POSITIVE_THRESHOLD = 0.72;
/** Criterion satisfaction ≤ this → surfaced as a concern. */
export const REASON_CONCERN_THRESHOLD = 0.4;
/** Max reasons / concerns attached to a match (keep explanations scannable). */
export const MAX_REASONS = 6;
export const MAX_CONCERNS = 3;
